import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["trigger", "menu", "field", "apply", "cancel"]
  static values = { portal: Boolean, align: String }

  connect() {
    document.addEventListener("click", this.closeOnClickOutside)
    document.addEventListener("keydown", this.closeOnEscape)
    document.addEventListener("scroll", this.reposition, true)
    window.addEventListener("resize", this.reposition)
    if (this.hasApplyTarget) this.applyTarget.addEventListener("click", this.apply)
    if (this.hasCancelTarget) this.cancelTarget.addEventListener("click", this.cancel)
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnClickOutside)
    document.removeEventListener("keydown", this.closeOnEscape)
    document.removeEventListener("scroll", this.reposition, true)
    window.removeEventListener("resize", this.reposition)
    this.restoreMenu()
    if (this.hasApplyTarget) this.applyTarget.removeEventListener("click", this.apply)
    if (this.hasCancelTarget) this.cancelTarget.removeEventListener("click", this.cancel)
  }

  closeOnClickOutside = (event) => {
    const insidePopover = this.element.contains(event.target) || this.portaledMenu?.contains(event.target)
    if (!this.element.open) return

    const link = event.target.closest?.("a")
    if (!insidePopover || link) this.close()
  }

  closeOnEscape = (event) => {
    if (event.key !== "Escape" || !this.element.open) return

    this.close()
    this.triggerTarget?.focus()
  }

  toggle() {
    if (!this.portalValue || !this.hasMenuTarget || !this.hasTriggerTarget) return

    if (this.element.open) this.portalMenu()
    else this.restoreMenu()
  }

  close() {
    this.element.removeAttribute("open")
    this.restoreMenu()
  }

  apply = () => {
    this.close()
  }

  cancel = () => {
    this.clearAndClose()
  }

  clearAndClose() {
    const field = this.portaledMenu?.querySelector('[data-popover-target~="field"]') ||
      (this.hasFieldTarget ? this.fieldTarget : null)

    if (field) {
      field.value = ""
      field.dispatchEvent(new Event("input", { bubbles: true }))
    }

    this.close()
  }

  portalMenu() {
    if (this.portaledMenu) return this.positionMenu()

    this.menuPlaceholder = document.createComment("popover-menu")
    this.menuTarget.before(this.menuPlaceholder)
    this.portaledMenu = this.menuTarget
    this.menuTarget.style.position = "fixed"
    document.body.append(this.menuTarget)
    this.positionMenu()
  }

  positionMenu() {
    if (!this.portaledMenu || !this.element.open) return

    const triggerRect = this.triggerTarget.getBoundingClientRect()
    const menu = this.portaledMenu
    const gutter = 8
    const offset = 4

    menu.style.visibility = "hidden"
    menu.style.right = "auto"
    menu.style.margin = "0"
    menu.style.zIndex = "100"

    const menuRect = menu.getBoundingClientRect()
    const fitsAbove = triggerRect.top - menuRect.height - offset >= gutter
    const overflowsBelow = triggerRect.bottom + offset + menuRect.height > window.innerHeight - gutter
    const top = overflowsBelow && fitsAbove ? triggerRect.top - menuRect.height - offset : triggerRect.bottom + offset
    const preferredLeft = this.alignValue === "start" ? triggerRect.left : triggerRect.right - menuRect.width
    const left = Math.min(
      Math.max(preferredLeft, gutter),
      window.innerWidth - menuRect.width - gutter
    )

    menu.style.top = `${Math.round(top)}px`
    menu.style.left = `${Math.round(left)}px`
    menu.style.visibility = "visible"
  }

  reposition = () => {
    if (this.element.open && this.portaledMenu) this.positionMenu()
  }

  restoreMenu() {
    if (!this.portaledMenu || !this.menuPlaceholder) return

    this.resetMenuStyles()
    this.menuPlaceholder.replaceWith(this.portaledMenu)
    this.menuPlaceholder = null
    this.portaledMenu = null
  }

  resetMenuStyles() {
    const properties = ["position", "visibility", "right", "margin", "z-index", "top", "left"]

    properties.forEach((property) => {
      this.portaledMenu.style.removeProperty(property)
    })
  }
}
